import { isValid } from 'date-fns';
import { getInputBorderStyle } from 'lib/BaseInput';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DayPicker, CaptionProps } from 'react-day-picker';
import cx from 'classnames';

import './DateTimePicker.pcss';
import CalendarCaption from 'lib/DatePicker/CalendarCaption';
import {
  DATE_TIME_PICKER_YEARS_AFTER,
  DATE_TIME_PICKER_YEARS_BEFORE,
  FIRST_DAY_OF_THE_WEEK,
} from 'consts/dateTimePicker';
import { getTimeFromDate } from 'utils/getTimeFromDate';
import { DateTimePickerProps } from './DateTimePicker.types';

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  isOpen,
  onChange,
  disabledDays,
  fromMonth,
  toMonth,
  isDisabled = false,
  firstDayOfTheWeek = FIRST_DAY_OF_THE_WEEK,
  yearsBefore = DATE_TIME_PICKER_YEARS_BEFORE,
  yearsAfter = DATE_TIME_PICKER_YEARS_AFTER,
  seconds = 0,
  milliseconds = 0,
  isTimePickerEnabled = true,
  isMinutesSelection = true,
  width = 'w-[320px]',
  navigationWidth = 'w-[296px]',
}) => {
  const [month, setMonth] = useState(new Date());

  const isValidDate = value != null && isValid(value);

  useEffect(() => {
    if (isValidDate) setMonth(value);
  }, [isOpen, isValidDate]);

  const time = useMemo((): string => getTimeFromDate(value), [value, isValidDate]);

  const handleYearMonthChange = useCallback((newDate: Date): void => setMonth(newDate), []);

  const handleDateChange = useCallback(
    (newDate: Date): void => {
      const date = new Date(newDate);
      date.setHours(isValidDate ? value.getHours() : 0, isValidDate ? value.getMinutes() : 0, seconds, milliseconds);

      onChange(date, 'DATE');
    },
    [value, isValidDate],
  );

  const handleTimeChange = useCallback(
    (dateTime: string): void => {
      if (!value) return;

      const newDate = new Date(value);
      const [hours, minutes] = dateTime.split(':');

      newDate.setHours(+hours, isMinutesSelection ? +minutes : 0, seconds, milliseconds);

      onChange(newDate, 'TIME');
    },
    [value, isMinutesSelection],
  );

  const captionPropsRef = useRef({ yearsBefore, yearsAfter, navigationWidth });
  captionPropsRef.current = { yearsBefore, yearsAfter, navigationWidth };

  const CaptionComponent = useMemo(() => {
    const stableCaption = (props: CaptionProps): React.ReactElement => (
      <CalendarCaption
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        onMonthChange={handleYearMonthChange}
        yearsBefore={captionPropsRef.current.yearsBefore}
        yearsAfter={captionPropsRef.current.yearsAfter}
        navigationWidth={captionPropsRef.current.navigationWidth}
      />
    );
    stableCaption.displayName = 'StableCaption';
    return stableCaption;
  }, [handleYearMonthChange]);

  return (
    <div className={cx('DateTimePicker flex flex-col items-center', width)} data-test-id="date-time-picker-calendar">
      <DayPicker
        showOutsideDays
        weekStartsOn={firstDayOfTheWeek}
        selected={value}
        disabled={disabledDays}
        month={month}
        onMonthChange={setMonth}
        fromMonth={fromMonth}
        toMonth={toMonth}
        onDayClick={(day) => handleDateChange(day)}
        components={{ Caption: CaptionComponent }}
      />
      {isTimePickerEnabled ? (
        <div className="flex justify-center my-4">
          <input
            className={cx(
              getInputBorderStyle({ isDisabled }),
              'text-sm text-center p-1 outline-none bg-neutral-50 w-min',
            )}
            type="time"
            data-test-id="date-time-picker-time-input"
            onChange={(e) => handleTimeChange(e.target.value)}
            value={time}
            disabled={!isValidDate}
          />
        </div>
      ) : null}
    </div>
  );
};

export default DateTimePicker;
